/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, type ReactNode } from 'react';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { cn } from '@/lib/utils';

/**
 * The section a page's breadcrumb renders in, gated by the sitewide `features.breadcrumbs` flag.
 *
 * When the flag is off (the default) this renders **nothing** — the whole wrapper, including whatever
 * spacing is passed via `className` (e.g. `section-container pt-6`), is dropped so no gap is left above
 * the next element (the top-most content / Page Designer component). When on, it renders the wrapper
 * with its children. Gate the *entire* breadcrumb section with this — pass the section's own spacing
 * as `className` — so show/hide is a single global switch and hiding never leaves an empty band.
 */
export function BreadcrumbSection({
    className,
    children,
}: {
    className?: string;
    children: ReactNode;
}): ReactElement | null {
    const config = useConfig();
    // Optional-chained + default-hidden: matches the sitewide default (off) and stays safe if a
    // config source hasn't populated `features` yet.
    if (!config.features?.breadcrumbs?.enabled) {
        return null;
    }
    return <div className={cn(className)}>{children}</div>;
}
